import { EventEmitter } from "events";

// never[] params: accepts any concrete listener without `any`; args are forwarded via Reflect.apply.
type Listener = (...args: never[]) => unknown;
type ReplyFn = (response: unknown) => unknown;

const isReplyFn = (value: unknown): value is ReplyFn => typeof value === "function";

const isThenable = (value: unknown): value is PromiseLike<unknown> =>
  typeof value === "object" && value !== null && "then" in value && typeof value.then === "function";

const stringField = (source: unknown, key: "message" | "name"): string | undefined => {
  if (typeof source !== "object" || source === null || !(key in source)) return undefined;
  const value: unknown = (source as Record<typeof key, unknown>)[key];
  return typeof value === "string" ? value : undefined;
};

/**
 * The error envelope a failed command handler replies with. Same shape the page
 * `registerHandler` produces, so callers that already unwrap an engine error keep working.
 */
export function errorReply(error: unknown): { success: false; error: string; errorType: string } {
  const reason = stringField(error, "message")?.trim() || stringField(error, "name") || "handler failed";
  return { success: false, error: reason, errorType: "engine_error" };
}

/**
 * #803 — a command's reply travels ONLY through the callback passed as the last argument to the
 * listener. A handler that throws (or rejects) before calling it leaves the caller waiting for the
 * full 10s `postMessageWithResponse` timeout — on a WhatsApp Web build that dropped a store module this
 * made whole UI surfaces (e.g. the campaign launcher) silently dead. This emitter wraps every
 * registered listener so a sync throw or async rejection is always answered with an error reply.
 * A handler that already replied is unaffected (a second resolve of the reply promise is a no-op).
 */
export class ReplySafeEmitter extends EventEmitter {
  private static wrap(event: string | symbol, listener: Listener): (...args: unknown[]) => void {
    const wrapped: ((...args: unknown[]) => void) & { listener?: Listener } = function (this: unknown, ...args: unknown[]) {
      const reply = args[args.length - 1];
      const fail = (error: unknown) => {
        console.error(`[ReplySafeEmitter] cmd=${String(event)} handler failed; replying with an error:`, error);
        if (isReplyFn(reply)) {
          try { reply(errorReply(error)); } catch { /* the reply channel is gone */ }
        }
      };
      try {
        const result: unknown = Reflect.apply(listener, this, args);
        if (isThenable(result)) result.then(undefined, fail);
      } catch (error) {
        fail(error);
      }
    };
    wrapped.listener = listener; // lets removeListener(event, original) find it
    return wrapped;
  }

  override on(event: string | symbol, listener: Listener): this {
    return super.on(event, ReplySafeEmitter.wrap(event, listener));
  }

  override addListener(event: string | symbol, listener: Listener): this {
    return super.addListener(event, ReplySafeEmitter.wrap(event, listener));
  }
}
